import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileText, Pencil, Plus, Share2, ShieldCheck } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { P } from "@/components/studio/portalKit";
import { InspectionBoard } from "@/pages/Inspection";

const pdfUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/inspections/${id}/report.pdf`;
const waUrl = (r) => {
  const ph = (r.customer_phone || "").replace(/\D/g, "");
  const msg = `Hi ${r.customer_name || ""}, here is your vehicle inspection report: ${pdfUrl(r.id)}`;
  return `https://wa.me/${ph.length === 10 ? `91${ph}` : ph}?text=${encodeURIComponent(msg)}`;
};

const ReportRow = ({ r, onEdit }) => {
  const nav = useNavigate();
  return (
    <Card data-testid={`insp-row-${r.id}`}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-semibold text-brand-ink">{r.customer_name} · {r.car_details || "Vehicle"}</p>
          <p className="text-xs text-slate-500">{fmtDate(r.updated_at)} · Exterior {r.exterior_count} · Interior {r.interior_count} marks</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <Button size="sm" variant="outline" className="h-10" data-testid={`insp-edit-${r.id}`} onClick={() => onEdit(r.studio_customer_id)}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
          <a href={pdfUrl(r.id)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-10 w-full" data-testid={`insp-pdf-${r.id}`}><FileText className="mr-1 h-3.5 w-3.5" />PDF</Button></a>
          <a href={waUrl(r)} target="_blank" rel="noreferrer"><Button size="sm" className="h-10 w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`insp-share-${r.id}`}><Share2 className="mr-1 h-3.5 w-3.5" />WhatsApp</Button></a>
          <Button size="sm" className="h-10 bg-brand-deep hover:bg-brand-deep/90" data-testid={`insp-create-warranty-${r.id}`} onClick={() => nav(`/studio/warranty?customer=${r.studio_customer_id}`)}>
            <ShieldCheck className="mr-1 h-3.5 w-3.5" />Create Warranty
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const CustomerPicker = ({ customers, onPick, onCancel }) => (
  <Card><CardContent className="space-y-3 p-4">
    <p className="font-semibold text-brand-ink">Choose the customer</p>
    {customers.length === 0 && <p className="text-sm text-slate-500">Add the customer on the Customers page first.</p>}
    <div className="space-y-2">
      {customers.map((c) => (
        <button key={c.id} data-testid={`insp-pick-${c.id}`} onClick={() => onPick(c.id)}
          className="flex min-h-[48px] w-full items-center justify-between rounded-lg border border-brand-line px-3 text-left text-sm transition-colors hover:border-crimson-500">
          <span className="font-medium">{c.name}</span><span className="text-xs text-slate-500">{[c.brand, c.model, c.reg_no].filter(Boolean).join(" · ")}</span>
        </button>
      ))}
    </div>
    <Button variant="ghost" onClick={onCancel}>Cancel</Button>
  </CardContent></Card>
);

export default function StudioInspection() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [params, setParams] = useSearchParams();
  const [picking, setPicking] = useState(false);
  const cid = params.get("customer");
  const load = () => api.get(`${P}/inspection-reports`).then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get(`${P}/customers`).then(({ data }) => setCustomers(data)); }, []);
  const open = (id) => { setPicking(false); setParams({ customer: id }); };
  const close = () => { setParams({}); load(); };
  const cust = customers.find((c) => c.id === cid);
  if (cid) {
    return (
      <InspectionBoard key={cid} loadUrl={`${P}/customers/${cid}/inspection`} saveUrl={`${P}/customers/${cid}/inspection/generate`} onBack={close}
        header={cust && <div data-testid="insp-customer-header" className="mx-4 mt-3 rounded-lg bg-brand-tint px-3 py-2 text-sm"><b>{cust.name}</b> · {cust.mobile}<br />{[cust.brand, cust.model, cust.reg_no].filter(Boolean).join(" · ")}</div>} />
    );
  }
  return (
    <div className="space-y-6">
      <PageHead title="Vehicle Inspection Report" subtitle="Mark scratches, dents and stains on the car diagram and share the PDF with the customer.">
        {!picking && <Button data-testid="new-inspection-btn" onClick={() => setPicking(true)} className="h-11 bg-crimson-500 hover:bg-crimson-600"><Plus className="mr-1.5 h-4 w-4" />New report</Button>}
      </PageHead>
      {picking && <CustomerPicker customers={customers} onPick={open} onCancel={() => setPicking(false)} />}
      <div className="space-y-3">
        {rows.length === 0 && !picking && <p className="text-sm text-slate-500">No reports yet.</p>}
        {rows.map((r) => <ReportRow key={r.id} r={r} onEdit={open} />)}
      </div>
    </div>
  );
}
